/* eslint-disable react-hooks/refs --
   The whole point of this hook is the "always-fresh ref" pattern:
   userRef.current = user every render keeps the ref pointing at the
   latest user, so the formatter closures (memoized with [] for stable
   identity) always read the current user without invalidating their
   memo. The rule flags ref-current writes during render universally;
   here it's the deliberate technique. */
// frontend/src/hooks/useFormatters.jsx
import { useMemo, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { formatNumber, formatDate, formatDateTime, formatCurrency, formatCompact, formatMonthYear, formatPeriodLabel, parseLocaleNumber } from '../utils/formatters';

/**
 * Custom hook that provides user-specific formatting functions.
 *
 * Returns STABLE function references via a ref pattern — the functions never
 * change identity even when userContext updates (e.g. theme toggle saves to
 * backend and replaces the user object). This prevents AG Grid datasources
 * and column-def callbacks from reloading data on unrelated context updates.
 *
 * The ref is updated on every render so the formatters always use the latest
 * user preferences (locale, date format, etc.).
 *
 * DISPLAY ONLY. There is deliberately no "today" here: a business date the
 * merchant acts on belongs to the STORE's calendar, not the viewer's profile,
 * so it comes from `useStoreToday` instead.
 */
export function useFormatters() {
  const { user } = useAuth();

  const userRef = useRef(user);
  userRef.current = user;

  return useMemo(() => ({
    formatNumber: (value, decimals = 2) => formatNumber(value, userRef.current, decimals),
    formatDate: (date) => formatDate(date, userRef.current),
    formatDateTime: (date) => formatDateTime(date, userRef.current),
    formatCurrency: (value, currency) => formatCurrency(value, currency, userRef.current),
    formatCompact: (value, currency) => formatCompact(value, currency, userRef.current),
    formatMonthYear: (date) => formatMonthYear(date, userRef.current),
    formatPeriodLabel: (date, bucket) => formatPeriodLabel(date, bucket, userRef.current),
    parseNumber: (value) => parseLocaleNumber(value, userRef.current),
  }), []);
}
